import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import AccountWizard from '../components/AccountWizard/AccountWizard';
import PlatformBadge from '../components/PlatformBadge';
import apiClient from '../services/api';
import useResource from '../services/useResource';
import { AccountGroup, MaybePaginated, SocialAccount } from '../types/api';
import './AccountsPage.css';

export default function AccountsPage() {
  const [actionError, setActionError] = useState<string | null>(null);
  const [oauthFlash, setOauthFlash] = useState<{ ok: boolean; message: string } | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const [newGroupName, setNewGroupName] = useState('');

  const { data: groups, reload: reloadGroups } = useResource<AccountGroup[], MaybePaginated<AccountGroup>>(
    () => apiClient.get<MaybePaginated<AccountGroup>>('/accounts/groups/'),
    [],
    { initial: [], transform: (data) => (Array.isArray(data) ? data : data.results || []) },
  );

  async function createGroup(): Promise<void> {
    if (!newGroupName.trim()) return;
    try {
      await apiClient.post('/accounts/groups/', { name: newGroupName.trim() });
      setNewGroupName('');
      reloadGroups();
    } catch (err: any) {
      setActionError(err?.body?.detail || err?.message || 'Failed to create group');
    }
  }

  async function deleteGroup(id: number): Promise<void> {
    if (!window.confirm('Delete this group? Accounts will be ungrouped.')) return;
    try {
      await apiClient.delete(`/accounts/groups/${id}/`);
      reloadGroups();
      reload();
    } catch (err: any) {
      setActionError(err?.message || 'Failed to delete group');
    }
  }

  async function assignGroup(accountId: number, groupId: number | null): Promise<void> {
    try {
      if (groupId) {
        await apiClient.post(`/accounts/groups/${groupId}/assign/`, { account_ids: [accountId] });
      } else {
        await apiClient.post('/accounts/groups/unassign/', { account_ids: [accountId] });
      }
      reload();
      reloadGroups();
    } catch (err: any) {
      setActionError(err?.message || 'Failed to assign group');
    }
  }

  const { data: accounts, loading, error, reload } = useResource<SocialAccount[], MaybePaginated<SocialAccount>>(
    () => apiClient.get<MaybePaginated<SocialAccount>>('/accounts/social-accounts/'),
    [],
    { initial: [], transform: (data) => (Array.isArray(data) ? data : data.results || []) },
  );

  // Surface ?connected=instagram / ?oauth_error=... from the OAuth callback,
  // then clear the params so a refresh doesn't re-flash the banner.
  useEffect(() => {
    const connected = searchParams.get('connected');
    const oauthError = searchParams.get('oauth_error');
    if (connected) {
      setOauthFlash({ ok: true, message: `Connected ${connected}.` });
      reload();
    } else if (oauthError) {
      setOauthFlash({ ok: false, message: `OAuth failed: ${oauthError}` });
    }
    if (connected || oauthError) {
      const next = new URLSearchParams(searchParams);
      next.delete('connected');
      next.delete('oauth_error');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function toggle(id: number): Promise<void> {
    setActionError(null);
    try {
      await apiClient.post(`/accounts/social-accounts/${id}/toggle_active/`, {});
      reload();
    } catch (err) {
      setActionError((err as Error).message || 'Toggle failed');
    }
  }

  async function remove(id: number): Promise<void> {
    if (!window.confirm('Disconnect this account?')) return;
    setActionError(null);
    try {
      await apiClient.delete(`/accounts/social-accounts/${id}/`);
      reload();
    } catch (err) {
      setActionError((err as Error).message || 'Delete failed');
    }
  }

  const errorMessage = actionError || error?.message;

  return (
    <div className="accounts-page">
      <h2>Accounts</h2>
      {errorMessage && <div className="accounts-error">{errorMessage}</div>}
      {oauthFlash && (
        <div className={oauthFlash.ok ? 'posts-info' : 'accounts-error'}>
          {oauthFlash.message}
        </div>
      )}

      <AccountWizard onConnected={reload} />

      <section className="accounts-list">
        <h3>Groups</h3>
        <div className="group-create-row">
          <input
            type="text"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            placeholder="New group name"
            className="group-create-input"
            onKeyDown={(e) => e.key === 'Enter' && createGroup()}
          />
          <button className="btn btn--sm btn--primary" onClick={createGroup} disabled={!newGroupName.trim()}>
            Add
          </button>
        </div>
        {groups.length > 0 && (
          <ul className="group-list">
            {groups.map((g) => (
              <li key={g.id} className="group-row">
                <span>{g.name}</span>
                <span className="muted">{g.accounts.length} accounts</span>
                <button className="btn btn--danger btn--sm" onClick={() => deleteGroup(g.id)}>×</button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="accounts-list">
        <h3>Connected ({accounts.length})</h3>
        {loading && <p className="muted">Loading…</p>}
        {!loading && accounts.length === 0 && (
          <p className="muted">No accounts connected yet.</p>
        )}
        <ul>
          {accounts.map((a) => (
            <li key={a.id} className={`account-row ${a.is_active ? '' : 'inactive'}`}>
              <div className="account-info">
                <PlatformBadge platform={a.platform} />
                <span className="account-name">{a.account_name}</span>
                {!a.is_active && <span className="muted">disabled</span>}
              </div>
              <div className="account-actions">
                <select
                  className="group-select"
                  value={a.group || ''}
                  onChange={(e) => assignGroup(a.id, e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">No group</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
                <button className="ghost btn--sm" onClick={() => toggle(a.id)}>
                  {a.is_active ? 'Disable' : 'Enable'}
                </button>
                <button className="danger" onClick={() => remove(a.id)}>Remove</button>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
